// Package imports for React hooks and utilities
import React, { useState, useEffect, useMemo, useCallback } from "react";
import cx from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faAngleRight } from "@fortawesome/pro-regular-svg-icons";

// Component imports for UI elements
import DropSelect from "../ui-elements/DropSelect/DropSelect";

/**
 * Configuration interface for pagination dropdown behavior
 * Controls how the items-per-page selector behaves
 */
interface IPaginationConfig {
  itemsPerPage?: number; // Default number of items per page
  onItemsPerPageChange?: (value: number) => void; // Callback when items per page changes
  itemsPerPageOptions?: Array<{ label: string; value: string }>; // Custom dropdown options
  usePortal?: boolean; // Whether to render dropdown in a portal (for z-index issues)
}

/**
 * Configuration interface for chunked data fetching
 * Used when you need to load data in chunks rather than all at once
 */
interface IChunkedFetchConfig {
  chunkSize: number; // Number of items to fetch in each chunk (e.g., 500)
  onFetchMoreData?: (startIndex: number, chunkSize: number) => Promise<any[]>; // Function to fetch more data
  onFiltersChange?: () => void; // Called when filters change to reset the data state
  onItemsPerPageChange?: (itemsPerPage: number) => void; // Called when page size changes
  prefetchThreshold?: number; // How many pages ahead to prefetch data (default: 1)
}

/**
 * Core interface for pagination state and control functions
 * Contains all the information and actions needed for pagination
 */
interface IPaginationState {
  currentPageIndex: number; // Current page (0-based indexing)
  itemsPerPage: number; // Number of items shown per page
  totalItems: number; // Total number of items across all pages
  totalPages: number; // Total number of pages calculated from totalItems and itemsPerPage
  startIndex: number; // Starting index for current page in the full dataset
  endIndex: number; // Ending index for current page in the full dataset
  isNextPageDisabled: boolean; // Whether the next button should be disabled
  isPrevPageDisabled: boolean; // Whether the previous button should be disabled
  goToNextPage: () => void; // Function to navigate to next page
  goToPreviousPage: () => void; // Function to navigate to previous page
  goToPage: (pageIndex: number) => void; // Function to jump to specific page
  setItemsPerPage: (itemsPerPage: number) => void; // Function to change items per page
  reset: () => void; // Function to reset pagination to initial state
}

/**
 * Extended interface for chunked pagination that includes data management
 * Used when working with large datasets that are loaded incrementally
 */
interface IChunkedPaginationState extends IPaginationState {
  currentData: any[]; // The subset of data for the current page
  isLoadingMore: boolean; // Whether we're currently fetching additional data
  hasMoreData: boolean; // Whether there's more data available to fetch
  loadedItemsCount: number; // Total number of items currently loaded into memory
}

/**
 * Props interface for the pagination dropdown component
 * Used to configure the items-per-page selector
 */
interface IPaginationDropdownProps {
  paginationState: IPaginationState; // The pagination state object
  paginationConfig?: IPaginationConfig; // Optional configuration for dropdown behavior
  className?: string; // Additional CSS classes for styling
  label?: string; // Label text displayed next to the dropdown
  width?: string; // CSS width value for the dropdown
  disabled?: boolean; // Whether the dropdown should be disabled
}

/**
 * Props interface for the pagination flipper component
 * Used to configure the page navigation arrows and display
 */
interface IPaginationFlipperProps {
  paginationState: IPaginationState; // The pagination state object
  className?: string; // Additional CSS classes for styling
  pageLabel?: string; // Template for page info display (supports {current} and {total} placeholders)
  hideIfNoPages?: boolean; // Whether to hide the component when there's only one page
}

/**
 * Custom hook for managing basic pagination state and logic
 * Handles all the calculations and state management for pagination
 * Works with both client-side and server-side pagination scenarios
 * 
 * @param totalItems - Total number of items across all pages
 * @param initialItemsPerPage - Starting number of items per page (default: 10)
 * @param initialPageIndex - Starting page index, 0-based (default: 0)
 * @param onPageChange - Optional callback fired when page changes (useful for server-side pagination)
 * @param onItemsPerPageChange - Optional callback fired when items per page changes
 * @returns Complete pagination state object with all necessary data and functions
 */
export const usePagination = (
  totalItems: number,
  initialItemsPerPage: number = 10,
  initialPageIndex: number = 0,
  onPageChange?: (pageIndex: number) => void,
  onItemsPerPageChange?: (itemsPerPage: number) => void
): IPaginationState => {
  // State for tracking the current page (0-based indexing for easier array slicing)
  const [currentPageIndex, setCurrentPageIndex] = useState(initialPageIndex);
  // State for tracking how many items to show per page
  const [itemsPerPage, setItemsPerPageState] = useState(initialItemsPerPage);

  // Memoized calculation of pagination-related values to avoid unnecessary recalculations
  // Only recalculates when totalItems, itemsPerPage, or currentPageIndex changes
  const paginationInfo = useMemo(() => {
    // Calculate total pages, ensuring at least 1 page even with 0 items
    const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
    // Calculate the starting index for the current page in the full dataset
    const startIndex = currentPageIndex * itemsPerPage;
    // Calculate the ending index, making sure not to exceed total items
    const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

    return {
      totalPages,
      startIndex,
      endIndex,
    };
  }, [totalItems, itemsPerPage, currentPageIndex]);

  // Calculate whether navigation buttons should be disabled
  const isNextPageDisabled = currentPageIndex + 1 >= paginationInfo.totalPages;
  const isPrevPageDisabled = currentPageIndex === 0;

  // Reset to first page when totalItems changes (e.g., when new data is loaded or filters are applied)
  // This prevents being stuck on a page that no longer exists
  useEffect(() => {
    setCurrentPageIndex(0);
  }, [totalItems]);

  // Handle changes to items per page
  // Reset to first page and notify parent component of the change
  useEffect(() => {
    setCurrentPageIndex(0);
    if (onItemsPerPageChange) {
      onItemsPerPageChange(itemsPerPage);
    }
  }, [itemsPerPage, onItemsPerPageChange]);

  // Notify parent component when page changes
  // This is useful for server-side pagination where the parent needs to fetch new data
  useEffect(() => {
    if (onPageChange) {
      onPageChange(currentPageIndex);
    }
  }, [currentPageIndex, onPageChange]);

  // Navigation function to move to the next page
  // Includes safety check to prevent going beyond the last page
  const goToNextPage = () => {
    if (!isNextPageDisabled) {
      setCurrentPageIndex((prev) => prev + 1);
    }
  };

  // Navigation function to move to the previous page
  // Includes safety check to prevent going below the first page
  const goToPreviousPage = () => {
    if (!isPrevPageDisabled) {
      setCurrentPageIndex((prev) => prev - 1);
    }
  };

  // Function to jump directly to a specific page
  // Includes bounds checking to ensure the page index is valid
  const goToPage = (pageIndex: number) => {
    const clampedIndex = Math.max(0, Math.min(pageIndex, paginationInfo.totalPages - 1));
    setCurrentPageIndex(clampedIndex);
  };

  // Function to update the number of items per page
  // Includes validation to ensure a positive number
  const setItemsPerPage = (newItemsPerPage: number) => {
    if (newItemsPerPage > 0) {
      setItemsPerPageState(newItemsPerPage);
    }
  };

  // Function to reset pagination to its initial state
  // Useful when clearing filters or resetting the view
  const reset = () => {
    setCurrentPageIndex(initialPageIndex);
    setItemsPerPageState(initialItemsPerPage);
  };

  // Return the complete pagination state object
  return {
    currentPageIndex,
    itemsPerPage,
    totalItems,
    totalPages: paginationInfo.totalPages,
    startIndex: paginationInfo.startIndex,
    endIndex: paginationInfo.endIndex,
    isNextPageDisabled,
    isPrevPageDisabled,
    goToNextPage,
    goToPreviousPage,
    goToPage,
    setItemsPerPage,
    reset
  };
};

/**
 * Advanced custom hook for managing chunked pagination with data fetching
 * Used when working with large datasets that need to be loaded incrementally
 * Includes intelligent prefetching to improve user experience
 * 
 * @param totalItemsInDataset - Total number of items in the complete dataset
 * @param loadedData - Array of currently loaded data items
 * @param fetchConfig - Configuration object for chunked fetching behavior
 * @param initialItemsPerPage - Starting number of items per page (default: 10)
 * @param initialPageIndex - Starting page index, 0-based (default: 0)
 * @returns Extended pagination state with data management capabilities
 */
export const useChunkedPagination = function<T = any>(
  totalItemsInDataset: number,
  loadedData: T[],
  fetchConfig: IChunkedFetchConfig,
  initialItemsPerPage: number = 10,
  initialPageIndex: number = 0
): IChunkedPaginationState {
  // State for tracking the current page (0-based indexing)
  const [currentPageIndex, setCurrentPageIndex] = useState(initialPageIndex);
  // State for tracking how many items to display per page
  const [itemsPerPage, setItemsPerPageState] = useState(initialItemsPerPage);
  // State for tracking whether we're currently fetching more data
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // State to track which chunks are currently being fetched (prevents duplicate requests)
  const [fetchingChunks, setFetchingChunks] = useState<Set<number>>(new Set());

  // Set default prefetch threshold if not provided (how many pages ahead to prefetch)
  const prefetchThreshold = fetchConfig.prefetchThreshold || 1;

  // Memoized calculation of pagination values based on display settings
  // These calculations are based on the total dataset size, not just loaded data
  const paginationInfo = useMemo(() => {
    // Calculate total pages based on the complete dataset size
    const totalPages = Math.ceil(totalItemsInDataset / itemsPerPage) || 1;
    // Calculate start and end indices for the current page
    const startIndex = currentPageIndex * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalItemsInDataset);

    return {
      totalPages,
      startIndex,
      endIndex,
    };
  }, [totalItemsInDataset, itemsPerPage, currentPageIndex]);

  // Determine if there's more data available to fetch from the server
  const hasMoreData = useMemo(() => {
    return loadedData.length < totalItemsInDataset && totalItemsInDataset > 0;
  }, [loadedData.length, totalItemsInDataset]);

  // Extract the data for the current page from the loaded data array
  const currentData = useMemo(() => {
    return loadedData.slice(paginationInfo.startIndex, paginationInfo.endIndex);
  }, [loadedData, paginationInfo.startIndex, paginationInfo.endIndex]);

  // Calculate whether navigation buttons should be disabled
  const isNextPageDisabled = currentPageIndex + 1 >= paginationInfo.totalPages;
  const isPrevPageDisabled = currentPageIndex === 0;

  // Helper function to determine which chunk contains a specific item index
  // Used for calculating which chunks need to be fetched
  const getChunkIndexForItemIndex = useCallback((itemIndex: number): number => {
    return Math.floor(itemIndex / fetchConfig.chunkSize);
  }, [fetchConfig.chunkSize]);

  // Helper function to determine what chunks need to be fetched for specific pages
  // Returns an array of chunk information that needs to be fetched
  const shouldPrefetchForPages = useCallback((pageIndices: number[]): { chunkIndex: number; startIndex: number }[] => {
    const chunksToFetch: { chunkIndex: number; startIndex: number }[] = [];
    
    // Check each page to see if we need to fetch data for it
    for (const pageIndex of pageIndices) {
      // Calculate the data range needed for this page
      const pageStartIndex = pageIndex * itemsPerPage;
      const pageEndIndex = Math.min(pageStartIndex + itemsPerPage, totalItemsInDataset);
      
      // Skip pages that are beyond our dataset
      if (pageStartIndex >= totalItemsInDataset) continue;
      
      // Check if we have enough loaded data to display this entire page
      if (pageEndIndex > loadedData.length) {
        // We need more data - determine which chunk contains the missing data
        const requiredChunkIndex = getChunkIndexForItemIndex(loadedData.length);
        const chunkStartIndex = requiredChunkIndex * fetchConfig.chunkSize;
        
        // Only add chunks that aren't already being fetched and contain valid data
        if (!fetchingChunks.has(requiredChunkIndex) && chunkStartIndex < totalItemsInDataset) {
          // Avoid duplicate entries in the fetch list
          const alreadyInList = chunksToFetch.some(chunk => chunk.chunkIndex === requiredChunkIndex);
          if (!alreadyInList) {
            chunksToFetch.push({
              chunkIndex: requiredChunkIndex,
              startIndex: chunkStartIndex
            });
          }
        }
      }
    }
    
    return chunksToFetch;
  }, [itemsPerPage, totalItemsInDataset, loadedData.length, fetchingChunks, getChunkIndexForItemIndex, fetchConfig.chunkSize]);

  // Function to fetch a specific chunk of data from the server
  // Handles loading states and prevents duplicate requests
  const fetchChunk = useCallback(async (chunkIndex: number, startIndex: number) => {
    // Exit early if no fetch function provided or chunk is already being fetched
    if (!fetchConfig.onFetchMoreData || fetchingChunks.has(chunkIndex)) {
      return;
    }

    // Mark this chunk as being fetched to prevent duplicate requests
    setFetchingChunks(prev => new Set([...Array.from(prev), chunkIndex]));
    setIsLoadingMore(true);

    try {
      // Call the provided fetch function with chunk parameters
      await fetchConfig.onFetchMoreData(startIndex, fetchConfig.chunkSize);
    } catch (error) {
      console.error(`Error fetching chunk ${chunkIndex}:`, error);
    } finally {
      // Remove this chunk from the fetching set once complete
      setFetchingChunks(prev => {
        const newSet = new Set(Array.from(prev));
        newSet.delete(chunkIndex);
        return newSet;
      });
      
      // Only set loading to false if no other chunks are being fetched
      setFetchingChunks(currentFetching => {
        if (currentFetching.size === 0) {
          setIsLoadingMore(false);
        }
        return currentFetching;
      });
    }
  }, [fetchConfig, fetchingChunks]);

  // Function to prefetch data for multiple pages
  // Fetches all required chunks in parallel for better performance
  const prefetchDataForPages = useCallback(async (pageIndices: number[]) => {
    const chunksToFetch = shouldPrefetchForPages(pageIndices);
    
    if (chunksToFetch.length > 0) {
      // Fetch all needed chunks in parallel for faster loading
      await Promise.all(
        chunksToFetch.map(({ chunkIndex, startIndex }) => 
          fetchChunk(chunkIndex, startIndex)
        )
      );
    }
  }, [shouldPrefetchForPages, fetchChunk]);

  // Effect to prefetch data when the current page changes
  // Ensures current page data is available and preloads upcoming pages
  useEffect(() => {
    // Build list of pages that should have their data prefetched
    const pagesToPrefetch: number[] = [];
    
    // Always ensure current page data is available
    pagesToPrefetch.push(currentPageIndex);
    
    // Add future pages based on the prefetch threshold
    for (let i = 1; i <= prefetchThreshold; i++) {
      const nextPage = currentPageIndex + i;
      if (nextPage < paginationInfo.totalPages) {
        pagesToPrefetch.push(nextPage);
      }
    }
    
    // Only prefetch if we have some data loaded (avoid fetching on initial render)
    if (loadedData.length > 0) {
      prefetchDataForPages(pagesToPrefetch);
    }
  }, [currentPageIndex, prefetchThreshold, paginationInfo.totalPages, loadedData.length, prefetchDataForPages]);

  // Reset to first page when total dataset size changes (e.g., filters applied)
  useEffect(() => {
    setCurrentPageIndex(0);
    // Clear any pending chunk fetches when dataset changes
    setFetchingChunks(new Set());
  }, [totalItemsInDataset]);

  // Reset to first page when items per page changes
  useEffect(() => {
    setCurrentPageIndex(0);
    // Clear any pending chunk fetches when page size changes
    setFetchingChunks(new Set());
    
    // Notify parent component of the change
    if (fetchConfig.onItemsPerPageChange) {
      fetchConfig.onItemsPerPageChange(itemsPerPage);
    }
  }, [itemsPerPage, fetchConfig]);

  // Enhanced navigation function for next page with predictive prefetching
  const goToNextPage = useCallback(() => {
    if (!isNextPageDisabled) {
      const nextPageIndex = currentPageIndex + 1;
      setCurrentPageIndex(nextPageIndex);
      
      // Prefetch data for pages ahead of the new current page
      const pagesToPrefetch: number[] = [];
      for (let i = 1; i <= prefetchThreshold; i++) {
        const futurePageIndex = nextPageIndex + i;
        if (futurePageIndex < paginationInfo.totalPages) {
          pagesToPrefetch.push(futurePageIndex);
        }
      }
      
      // Trigger prefetch if there are pages to load
      if (pagesToPrefetch.length > 0) {
        prefetchDataForPages(pagesToPrefetch);
      }
    }
  }, [isNextPageDisabled, currentPageIndex, prefetchThreshold, paginationInfo.totalPages, prefetchDataForPages]);

  // Navigation function for previous page
  // Previous pages should already have their data loaded, so no prefetching needed
  const goToPreviousPage = useCallback(() => {
    if (!isPrevPageDisabled) {
      setCurrentPageIndex((prev) => prev - 1);
    }
  }, [isPrevPageDisabled]);

  // Enhanced function to jump to a specific page with predictive prefetching
  const goToPage = useCallback((pageIndex: number) => {
    // Ensure the target page index is within valid bounds
    const clampedIndex = Math.max(0, Math.min(pageIndex, paginationInfo.totalPages - 1));
    setCurrentPageIndex(clampedIndex);
    
    // Prefetch data for the target page and surrounding pages
    const pagesToPrefetch: number[] = [clampedIndex];
    for (let i = 1; i <= prefetchThreshold; i++) {
      const nextPage = clampedIndex + i;
      if (nextPage < paginationInfo.totalPages) {
        pagesToPrefetch.push(nextPage);
      }
    }
    
    // Trigger prefetch for the calculated pages
    prefetchDataForPages(pagesToPrefetch);
  }, [paginationInfo.totalPages, prefetchThreshold, prefetchDataForPages]);

  // Function to update items per page with validation
  const setItemsPerPage = useCallback((newItemsPerPage: number) => {
    if (newItemsPerPage > 0) {
      setItemsPerPageState(newItemsPerPage);
    }
  }, []);

  // Function to reset pagination and data fetching to initial state
  const reset = useCallback(() => {
    setCurrentPageIndex(initialPageIndex);
    setItemsPerPageState(initialItemsPerPage);
    setFetchingChunks(new Set());
    
    // Notify parent that filters have changed (triggers data reset)
    if (fetchConfig.onFiltersChange) {
      fetchConfig.onFiltersChange();
    }
  }, [initialPageIndex, initialItemsPerPage, fetchConfig]);

  // Return the complete pagination state with chunked data management
  return {
    currentPageIndex,
    itemsPerPage,
    totalItems: totalItemsInDataset,
    totalPages: paginationInfo.totalPages,
    startIndex: paginationInfo.startIndex,
    endIndex: paginationInfo.endIndex,
    isNextPageDisabled,
    isPrevPageDisabled,
    goToNextPage,
    goToPreviousPage,
    goToPage,
    setItemsPerPage,
    reset,
    // Extended properties specific to chunked pagination
    currentData,
    isLoadingMore,
    hasMoreData,
    loadedItemsCount: loadedData.length,
  };
};

/**
 * Standalone dropdown component for selecting items per page
 * Can be rendered anywhere in the component tree while sharing pagination state
 * Provides a consistent interface for changing the number of items displayed per page
 * 
 * @param paginationState - State object returned from usePagination or useChunkedPagination hook
 * @param paginationConfig - Configuration for dropdown options and portal usage
 * @param className - Additional CSS classes for custom styling
 * @param label - Label text displayed next to the dropdown (default: "Sýna:")
 * @param disabled - Whether the dropdown should be disabled (default: false)
 */
export const PaginationDropdown: React.FC<IPaginationDropdownProps> = ({
  paginationState,
  paginationConfig,
  className,
  label = "Sýna:",
  disabled = false,
}) => {
  const { itemsPerPage, setItemsPerPage } = paginationState;

  // Default options for the items per page dropdown
  // Can be overridden via paginationConfig.itemsPerPageOptions
  const defaultItemsPerPageOptions = [
    { label: "10 á síðu", value: "10" },
    { label: "25 á síðu", value: "25" },
    { label: "50 á síðu", value: "50" },
    { label: "100 á síðu", value: "100" },
  ];

  return (
    <div className={cx("pagination__dropdown", "KCL_pagination", className, {disabled})}>
      <span className="paragraph-small--bold">{label}</span>
      {/* Type-cast DropSelect to any to bypass TypeScript JSX component type issues */}
      {React.createElement(DropSelect as any, {
        usePortal: paginationConfig?.usePortal ?? false,
        className: "pagination-dropdown",
        options: paginationConfig?.itemsPerPageOptions ?? defaultItemsPerPageOptions,
        defaultValue: itemsPerPage.toString(),
        onChange: (option: string) => setItemsPerPage(Number(option)),
        size: "sm",
        disabled,
      })}
    </div>
  );
};

/**
 * Standalone flipper component for page navigation
 * Can be rendered anywhere in the component tree while sharing pagination state
 * Provides navigation arrows and displays current page information
 * 
 * @param paginationState - State object returned from usePagination or useChunkedPagination hook
 * @param className - Additional CSS classes for custom styling
 * @param pageLabel - Template string for page info display. Use {current} and {total} as placeholders (default: "síða {current} af {total}")
 * @param hideIfNoPages - Whether to hide the component when there's only one page or no pages (default: false)
 */
export const PaginationFlipper: React.FC<IPaginationFlipperProps> = ({
  paginationState,
  className,
  pageLabel = "síða {current} af {total}",
  hideIfNoPages = false,
}) => {
  const {
    currentPageIndex,
    totalPages,
    isNextPageDisabled,
    isPrevPageDisabled,
    goToNextPage,
    goToPreviousPage,
  } = paginationState;

  // Conditionally hide the component if there are no pages or only one page
  if (totalPages <= 1 && hideIfNoPages) {
    return null;
  }

  // Format the page label by replacing template placeholders with actual values
  // Converts 0-based page index to 1-based for user display
  const formattedPageLabel = pageLabel
    .replace("{current}", (currentPageIndex + 1).toString())
    .replace("{total}", totalPages.toString());

  return (
    <div className={cx("KCL_pagination", "pagination__flipper", className)}>
      {/* Previous page button with FontAwesome icon */}
      {/* Type-cast FontAwesomeIcon to any to bypass TypeScript JSX component type issues */}
      {React.createElement(FontAwesomeIcon as any, {
        className: cx("pagination_angle", "prev-page", { disabled: isPrevPageDisabled }),
        icon: faAngleLeft,
        onClick: isPrevPageDisabled ? undefined : goToPreviousPage,
      })}
      {/* Page information display */}
      <span className="pagination_text">{formattedPageLabel}</span>
      {/* Next page button with FontAwesome icon */}
      {React.createElement(FontAwesomeIcon as any, {
        className: cx("pagination_angle", "next-page", { disabled: isNextPageDisabled }),
        icon: faAngleRight,
        onClick: isNextPageDisabled ? undefined : goToNextPage,
      })}
    </div>
  );
};

/**
 * Utility function to create render functions for pagination components
 * Useful when you want to pass render functions around or conditionally render components
 * Creates partially applied render functions with the pagination state already bound
 * 
 * @param paginationState - State object returned from usePagination or useChunkedPagination hook
 * @param paginationConfig - Optional configuration for the dropdown component
 * @returns Object containing render functions for both dropdown and flipper components
 */
export const createPaginationRenderers = (
  paginationState: IPaginationState,
  paginationConfig?: IPaginationConfig
) => {
  // Create a render function for the dropdown component with pagination state pre-bound
  const renderDropdown = (props?: Partial<IPaginationDropdownProps>) =>
    React.createElement(PaginationDropdown as any, {
      paginationState,
      paginationConfig,
      ...props,
    });

  // Create a render function for the flipper component with pagination state pre-bound
  const renderFlipper = (props?: Partial<IPaginationFlipperProps>) =>
    React.createElement(PaginationFlipper as any, {
      paginationState,
      ...props,
    });

  return {
    renderDropdown,
    renderFlipper,
  };
};

// Export all TypeScript interfaces for external use
// This allows other modules to import and use these types for type safety
export type { 
  IPaginationConfig, 
  IPaginationState, 
  IPaginationDropdownProps, 
  IPaginationFlipperProps,
  IChunkedPaginationState,
  IChunkedFetchConfig
};